import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import useToken from '../../hooks/useToken';

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join('');
}

const NavBar = () => {
  const [fullName, setFullName] = useState(localStorage.getItem('fullName'));
  const [role, setRole] = useState(localStorage.getItem('role'));
  const [address, setAddress] = useState(localStorage.getItem('address'));
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const { token, refreshAccessToken, removeToken } = useToken();

  useEffect(() => {
    setFullName(localStorage.getItem('fullName'));
    setRole(localStorage.getItem('role'));
    setAddress(localStorage.getItem('address'));
  }, []);

  const handleLogout = async () => {
    let authToken = token || await refreshAccessToken();
    if (!authToken) {
      removeToken();
      navigate('/', { replace: true });
      return;
    }

    try {
      const response = await fetch('/api/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      removeToken();
      localStorage.clear();
      navigate('/', { replace: true });
      window.location.reload();

      if (!response.ok) {
        console.error('Logout failed:', await response.json());
      }
    } catch (error) {
      console.error('An error occurred during logout:', error);
    }
  };

  const handleSave = async () => {
    let authToken = token || await refreshAccessToken();
    if (!authToken) {
      removeToken();
      navigate('/', { replace: true });
      return;
    }

    try {
      const response = await fetch('/api/player/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ full_name: fullName, address }),
      });

      if (response.ok) {
        localStorage.setItem('fullName', fullName);
        localStorage.setItem('address', address);
        setShowModal(false);
      } else {
        console.error('Failed to update profile:', await response.json());
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  // TODO: DELETE THIS BEFORE PRODUCTION - Debug only
  const handleResetProgress = async () => {
    if (!window.confirm('⚠️ DEBUG: Reset your progress to the beginning?')) return;

    let authToken = token || await refreshAccessToken();
    if (!authToken) {
      removeToken();
      navigate('/', { replace: true });
      return;
    }

    try {
      const response = await fetch('/api/accounts/reset-progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (response.ok) {
        alert('Progress reset successfully! Redirecting to dashboard...');
        navigate('/dashboard', { replace: true });
        window.location.reload();
      } else {
        console.error('Failed to reset progress:', await response.json());
        alert('Failed to reset progress');
      }
    } catch (error) {
      console.error('Error resetting progress:', error);
      alert('Error resetting progress');
    }
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={1}
        sx={{
          backgroundColor: '#ffffff',
          color: '#333',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 3 } }}>
          {/* Brand */}
          <Typography
            component={Link}
            to="/"
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#5e35b1',
              textDecoration: 'none',
              fontFamily: 'Inter, sans-serif',
              '&:hover': { color: '#4527a0' },
            }}
          >
            DocuPen Platform
          </Typography>

          {/* Right side */}
          {fullName ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* Dashboard link */}
              <Button
                component={Link}
                to="/dashboard"
                sx={{
                  textTransform: 'none',
                  color: '#333',
                  fontWeight: 500,
                  '&:hover': { color: '#5e35b1', backgroundColor: 'transparent' },
                }}
              >
                Dashboard
              </Button>

              {/* Avatar chip — opens profile modal */}
              <Tooltip title="Edit profile">
                <Chip
                  avatar={
                    <Avatar
                      sx={{
                        bgcolor: '#5e35b1',
                        color: '#fff',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                      }}
                    >
                      {getInitials(fullName)}
                    </Avatar>
                  }
                  label={fullName}
                  onClick={() => setShowModal(true)}
                  variant="outlined"
                  sx={{
                    borderColor: '#5e35b1',
                    color: '#5e35b1',
                    fontWeight: 500,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#ede7f6' },
                  }}
                />
              </Tooltip>

              {/* TODO: DELETE BEFORE PRODUCTION - Debug only */}
              <Tooltip title="Debug: Reset Progress">
                <IconButton
                  size="small"
                  onClick={handleResetProgress}
                  sx={{ color: '#f57c00' }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Logout */}
              <Button
                variant="outlined"
                onClick={handleLogout}
                sx={{
                  textTransform: 'none',
                  borderColor: '#d32f2f',
                  color: '#d32f2f',
                  fontWeight: 500,
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#d32f2f', color: '#fff', borderColor: '#d32f2f' },
                }}
              >
                Logout
              </Button>
            </Box>
          ) : (
            <Button
              component={Link}
              to="/"
              sx={{ textTransform: 'none', color: '#5e35b1', fontWeight: 500 }}
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Profile Dialog */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Profile</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField
            label="Full Name"
            name="fullName"
            value={fullName || ''}
            onChange={(e) => setFullName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Affiliation"
            name="address"
            value={address || ''}
            onChange={(e) => setAddress(e.target.value)}
            fullWidth
            size="small"
            placeholder="Enter your institution or affiliation"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowModal(false)} sx={{ textTransform: 'none', color: '#666' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: '#5e35b1',
              '&:hover': { backgroundColor: '#4527a0' },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NavBar;
